import EducationSummary from "@/features/events/features/educations/EducationSummary";
import SurgerySummary from "@/features/events/features/surgeries/components/SurgerySummary";
import InjectionDetailsList from "@/features/injection/components/InjectionDetailsList";
import { useSelectNameById } from "@/features/profile/api/selectNameById";
import ShockwaveDetailsList from "@/features/shockwave/components/ShockwaveDetailsList";
import type { EducationT, SurgeryViewT } from "@/types/database/events";
import { useAuth } from "@/utils/auth";
import {
  ArrowLeftRight,
  Crosshair,
  Hammer,
  Mail,
  User,
  User2,
} from "lucide-react";
import IconDetail from "../../../components/common/IconDetail";
import type { TemplateSettingsSchemaT } from "../../../types";
import { useSurgeryById } from "../../events/features/surgeries/api/selectSurgeryById";

// Helper to render a clinician/contact detail if present and not self
function ClinicianDetail(
  { id, label, icon }: {
    id?: string | null;
    label: string;
    icon: React.ReactNode;
  },
) {
  const auth = useAuth();

  if (!id || id === auth.id) return null;
  const { data, isLoading } = useSelectNameById(id);
  return (
    <IconDetail
      icon={icon}
      words={!isLoading && `${data?.name}`}
      className="mb-1"
    />
  );
}

function EventsPreview({ settings }: { settings: TemplateSettingsSchemaT }) {
  return (
    <div className="pt-2">
      <hr className="my-2" />
      {/* Render event previews as before, with section titles and inlined mapping logic */}
      {settings.required_events?.map((event) => {
        switch (event) {
          case "injection": {
            const injectionView = {
              diary_id: "",
              tracker_id: "",
              event_id: null,
              injection_id: settings.injection_id ?? "",
              injecting_clinician_email: null,
              injecting_clinician_formal_name: null,
              injecting_clinician_id: settings.injecting_clinician_id ?? "",
              injecting_clinician_name: settings.injecting_clinician_id ?? "",
              injecting_clinician_number: null,
              injection_details: (settings.injection_details || []).map((
                d: Record<string, unknown>,
                i: number,
              ) => ({
                injection_detail_id: (d.injection_detail_id as string) ||
                  String(i),
                ...d,
              })),
              injection_method: settings.injection_method_id ?? null,
              injection_method_id: settings.injection_method_id ?? null,
              is_incomplete: null,
              referring_clinician_email: null,
              referring_clinician_formal_name: null,
              referring_clinician_id: settings.referring_clinician_id ?? "",
              referring_clinician_name: settings.referring_clinician_id ?? "",
              referring_clinician_number: null,
            };
            return (
              <div
                key="injection"
                className="space-y-2"
              >
                <div className="mb-1 font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                  Injection
                </div>
                <ClinicianDetail
                  id={settings.contact_id}
                  label="Contact"
                  icon={<Mail />}
                />
                {/* Show injection clinicians if not self */}
                <ClinicianDetail
                  id={settings.referring_clinician_id}
                  label="Referring Clinician"
                  icon={<User2 />}
                />
                <ClinicianDetail
                  id={settings.injecting_clinician_id}
                  label="Injecting Clinician"
                  icon={<User />}
                />
                <InjectionDetailsList
                  injectionDetails={injectionView.injection_details}
                />
              </div>
            );
          }
          case "shockwave": {
            const shockwaveView = {
              event_id: "",
              shockwave_id: settings.shockwave_id ?? "",
              is_incomplete: null,
              referring_clinician_email: null,
              referring_clinician_formal_name: null,
              referring_clinician_id: settings.referring_clinician_id ?? "",
              referring_clinician_name: settings.referring_clinician_id ?? "",
              referring_clinician_number: null,
              shockwave_details: (settings.shockwave_details || []).map((
                d: Record<string, unknown>,
                i: number,
              ) => ({
                shockwave_detail_id: (d.shockwave_detail_id as string) ||
                  String(i),
                ...d,
              })),
              shockwaving_clinician_email: null,
              shockwaving_clinician_formal_name: null,
              shockwaving_clinician_id: settings.shockwaving_clinician_id ?? "",
              shockwaving_clinician_name: settings.shockwaving_clinician_id ??
                "",
              shockwaving_clinician_number: null,
            };
            return (
              <div
                key="shockwave"
                className="space-y-2"
              >
                <div className="mb-1 font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                  Shockwave
                </div>
                <ClinicianDetail
                  id={settings.contact_id}
                  label="Contact"
                  icon={<Mail />}
                />

                <ClinicianDetail
                  id={settings.referring_clinician_id}
                  label="Surgery Referrer"
                  icon={<User2 />}
                />
                <ClinicianDetail
                  id={settings.shockwaving_clinician_id}
                  label="Shockwaving Clinician"
                  icon={<User />}
                />
                <ShockwaveDetailsList
                  shockwaveDetails={shockwaveView.shockwave_details}
                />
              </div>
            );
          }
          case "surgery": {
            // Only call the hook if surgery_option_id is a non-empty string
            const surgeryOptionId = settings.surgery_option_id;
            const shouldFetchSurgery = typeof surgeryOptionId === "string" &&
              surgeryOptionId.length > 0;
            const surgery = shouldFetchSurgery
              ? useSurgeryById(surgeryOptionId).data
              : undefined;

            const dummySurgery: SurgeryViewT = {
              surgery_id: null,
              surgery_option_id: null,
              orthopaedic_area: null,
              side: null,
              created_at: null,
              event_id: null,
              is_incomplete: null,
              operating_clinician_id: null,
              operating_clinician_name: null,
              referring_clinician_id: null,
              referring_clinician_name: null,
              surgery_name: null,
            };
            return (
              <div
                key="surgery"
                className="space-y-2"
              >
                <div className="mb-1 font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                  Surgery
                </div>
                <ClinicianDetail
                  id={settings.contact_id}
                  label="Contact"
                  icon={<Mail />}
                />

                <ClinicianDetail
                  id={settings.referring_clinician_id}
                  label="Surgery Referrer"
                  icon={<User2 />}
                />
                <ClinicianDetail
                  id={settings.operating_clinician_id}
                  label="Operating Clinician"
                  icon={<User />}
                />
                <IconDetail
                  icon={<ArrowLeftRight size={16} />}
                  words={settings.side ?? "NA"}
                />
                <IconDetail
                  icon={<Crosshair size={16} />}
                  words={settings.orthopaedic_area ?? "NA"}
                />
                <IconDetail
                  icon={<Hammer size={16} />}
                  words={shouldFetchSurgery && surgery?.surgery_name
                    ? surgery.surgery_name
                    : "NA"}
                />
              </div>
            );
          }
          case "education": {
            const dummyEducation: EducationT = {
              created_at: "created_at" in settings
                ? (settings.created_at as string)
                : "",
              educated_by: settings.educated_by ?? "",
              education_description: settings.education_description ?? null,
              education_id: settings.education_id ?? "",
              education_name: settings.education_name ?? "",
              event_id: settings.event_id ?? "",
            };
            return (
              <div
                key="education"
                className="space-y-2"
              >
                <div className="mb-1 font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                  Education
                </div>
                <ClinicianDetail
                  id={settings.contact_id}
                  label="Contact"
                  icon={<Mail />}
                />

                <ClinicianDetail
                  id={settings.educated_by}
                  label="Educated By"
                  icon={<User />}
                />
                <EducationSummary education={dummyEducation} />
              </div>
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}

export default EventsPreview;
